import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Typography,
  Table,
  Button,
  Tag,
  Space,
  Modal,
  message,
  Breadcrumb,
  Image,
  Select,
  Drawer,
  Form,
  Input,
  Switch,
  Checkbox,
  Divider
} from "antd";

import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import styled from "styled-components";

import { WHITE } from "../../constant/colors";
import {
  getTargetedEducationContentListAPI,
  createTargetedEducationContentAPI,
  updateTargetedEducationContentAPI,
  deleteTargetedEducationContentAPI,
  getBrandJourneyConfigurationsAPI
} from "../../api/api";
import { ImageUploadField } from "../../component/ImageUploadField";
import { StructuredContentEditor } from "../../component/StructuredContentEditor";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const PageContainer = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 24px;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const JOURNEY_STEPS = [
  { value: "prescription", label: "Prescription" },
  { value: "coverage", label: "Coverage" },
  { value: "assistance", label: "Assistance" },
  { value: "support", label: "Support" }
];

const TRIGGER_TYPES = [
  { value: "none", label: "None" },
  { value: "1st_fill", label: "1st Fill" },
  { value: "refill_reminder", label: "Refill Reminder" },
  { value: "x_days_post_fill", label: "X Days Post-Fill" }
];

const TargetedEducationConfiguration = () => {
  const [contentList, setContentList] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  // Local blob URL for cover image preview — avoids S3 private-bucket auth issues
  const [coverPreviewUrl, setCoverPreviewUrl] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [contentRes, brandsRes] = await Promise.all([
        getTargetedEducationContentListAPI(),
        getBrandJourneyConfigurationsAPI()
      ]);
      setContentList(contentRes.data);
      setBrands(brandsRes.data);
    } catch (error) {
      message.error("Failed to load education content");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const data = {
        ...values,
        channels: {
          email: values.channelEmail || false,
          sms: values.channelSms || false,
          inApp: values.channelInApp || false
        }
      };
      delete data.channelEmail;
      delete data.channelSms;
      delete data.channelInApp;

      if (editingItem) {
        await updateTargetedEducationContentAPI(editingItem.id, data);
        message.success("Content updated");
      } else {
        await createTargetedEducationContentAPI(data);
        message.success("Content created");
      }
      setDrawerVisible(false);
      setEditingItem(null);
      form.resetFields();
      fetchData();
    } catch (error) {
      if (!error.errorFields) message.error("Failed to save content");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    setCoverPreviewUrl(null);
    form.setFieldsValue({
      ...record,
      channelEmail: record.channels?.email,
      channelSms: record.channels?.sms,
      channelInApp: record.channels?.inApp
    });
    setDrawerVisible(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: `Delete "${record.title}"?`,
      icon: <ExclamationCircleOutlined />,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        await deleteTargetedEducationContentAPI(record.id);
        message.success("Content deleted");
        fetchData();
      }
    });
  };

  const columns = [
    {
      title: "Cover",
      dataIndex: "coverImageUrl",
      key: "cover",
      width: 80,
      render: (url) =>
        url ? <Image src={url} width={60} height={40} style={{ objectFit: "cover", borderRadius: 4 }} /> : "-"
    },
    { title: "Title", dataIndex: "title", key: "title", width: 200 },
    {
      title: "Brand",
      dataIndex: "brandJourneyConfiguration",
      key: "brand",
      width: 120,
      render: (id) => brands.find((b) => b.id === id)?.brandName || "-"
    },
    {
      title: "Journey Step",
      dataIndex: "journeyStep",
      key: "step",
      width: 120,
      render: (step) => (step ? <Tag>{step}</Tag> : "-")
    },
    {
      title: "Trigger",
      dataIndex: "triggerType",
      key: "trigger",
      width: 120,
      render: (type) => (type && type !== "none" ? <Tag color="blue">{type}</Tag> : "-")
    },
    {
      title: "Featured",
      dataIndex: "isFeatured",
      key: "featured",
      width: 80,
      render: (v) => (v ? <Tag color="gold">Featured</Tag> : "-")
    },
    {
      title: "Sendable",
      dataIndex: "isPatientSendable",
      key: "sendable",
      width: 80,
      render: (v) => (v ? <Tag color="cyan">Sendable</Tag> : "-")
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "active",
      width: 70,
      render: (v) => <Switch checked={v} size="small" disabled />
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDelete(record)} />
        </Space>
      )
    }
  ];

  return (
    <PageContainer>
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to="/settings">Settings</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Targeted Education</Breadcrumb.Item>
      </Breadcrumb>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "16px 0" }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Targeted Education Content
          </Title>
          <Text type="secondary">
            Create and manage educational content for patients, with targeting by journey step and event triggers.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingItem(null);
            setCoverPreviewUrl(null);
            form.resetFields();
            setDrawerVisible(true);
          }}
        >
          Create Content
        </Button>
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        <Table columns={columns} dataSource={contentList} rowKey="id" loading={loading} pagination={false} />
      </div>

      <Drawer
        title={editingItem ? "Edit Content" : "Create Content"}
        width={1020}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setEditingItem(null);
        }}
        destroyOnClose
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setDrawerVisible(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              Save
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          <div style={{ display: "flex", gap: 32 }}>
            {/* ── Left: Live Preview ──────────────────────────────── */}
            <div style={{ width: 320, flexShrink: 0 }}>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const previewImage = coverPreviewUrl || getFieldValue("coverImageUrl");
                  const pTitle = getFieldValue("title");
                  const pDesc = getFieldValue("description");
                  const pCategory = getFieldValue("category");
                  const pReadTime = getFieldValue("readTime");
                  const pMediaType = getFieldValue("mediaType");
                  const pFeatured = getFieldValue("isFeatured");
                  const pDrugName = getFieldValue("drugName");
                  const CATEGORY_LABELS = {
                    financialTools: "Financial Tools",
                    educationAndSupport: "Education & Support",
                    communitiesAndAdvocacy: "Communities & Advocacy",
                    logisticsAndTransport: "Logistics & Transport",
                    wellness: "Wellness",
                    expertAccess: "Expert Access"
                  };

                  return (
                    <div style={{ position: "sticky", top: 20 }}>
                      <div style={{ color: "#0C85A3", fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                        LIVE PREVIEW
                      </div>
                      <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
                        How this content will appear in TM Connect
                      </Text>

                      {/* Phone frame */}
                      <div
                        style={{
                          background: "#f0f2f5",
                          borderRadius: 36,
                          padding: "14px 8px 10px",
                          boxShadow: "0 2px 20px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)",
                          border: "1px solid #e0e0e0"
                        }}
                      >
                        <div
                          style={{
                            borderRadius: 24,
                            overflow: "hidden",
                            background: "#F6F2ED",
                            height: 560,
                            display: "flex",
                            flexDirection: "column",
                            boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)"
                          }}
                        >
                          {/* Header */}
                          <div
                            style={{
                              background: "#fff",
                              padding: "8px 12px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              borderBottom: "1px solid #f0f0f0",
                              flexShrink: 0
                            }}
                          >
                            <Text strong style={{ fontSize: 11, color: "#0C85A3" }}>
                              Education
                            </Text>
                            <div
                              style={{
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                background: "#EBF9FD",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 9
                              }}
                            >
                              👤
                            </div>
                          </div>

                          {/* Scrollable content */}
                          <div style={{ flex: 1, overflowY: "auto" }}>
                            {/* Card Preview */}
                            <div
                              style={{
                                borderRadius: 16,
                                overflow: "hidden",
                                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                                background: "#fff",
                                marginBottom: 16
                              }}
                            >
                              {previewImage ? (
                                <div
                                  style={{
                                    height: 130,
                                    backgroundImage: `url(${previewImage})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    position: "relative"
                                  }}
                                >
                                  {pFeatured && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: 8,
                                        left: 8,
                                        background: "#389BB4",
                                        color: "#fff",
                                        fontSize: 10,
                                        fontWeight: 700,
                                        padding: "2px 8px",
                                        borderRadius: 20
                                      }}
                                    >
                                      Featured
                                    </div>
                                  )}
                                  {pMediaType && pMediaType !== "none" && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: 8,
                                        right: 8,
                                        background: "rgba(0,0,0,0.55)",
                                        color: "#fff",
                                        fontSize: 10,
                                        padding: "2px 8px",
                                        borderRadius: 20
                                      }}
                                    >
                                      {pMediaType === "video" ? "▶ Video" : "🎧 Audio"}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div
                                  style={{
                                    height: 130,
                                    background: "#F6F2ED",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                  }}
                                >
                                  <Text style={{ color: "#8F8C86", fontSize: 12 }}>Cover Image</Text>
                                </div>
                              )}
                              <div style={{ padding: "12px 14px" }}>
                                <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                                  {pDrugName && (
                                    <span
                                      style={{
                                        fontSize: 10,
                                        color: "#389BB4",
                                        background: "#EBF9FD",
                                        border: "1px solid #CDE6EC",
                                        borderRadius: 20,
                                        padding: "1px 8px",
                                        fontWeight: 700
                                      }}
                                    >
                                      {pDrugName}
                                    </span>
                                  )}
                                  {pReadTime && <span style={{ fontSize: 10, color: "#8F8C86" }}>📖 {pReadTime}</span>}
                                </div>
                                <Text
                                  strong
                                  style={{ fontSize: 14, color: "#393735", display: "block", lineHeight: 1.3 }}
                                >
                                  {pTitle || "Content Title"}
                                </Text>
                                {pDesc && (
                                  <Text
                                    style={{
                                      fontSize: 11,
                                      color: "#8F8C86",
                                      display: "block",
                                      marginTop: 4,
                                      lineHeight: 1.3
                                    }}
                                  >
                                    {pDesc.slice(0, 80)}
                                    {pDesc.length > 80 ? "..." : ""}
                                  </Text>
                                )}
                              </div>
                            </div>

                            {/* Detail Preview */}
                            <div
                              style={{
                                border: "1px solid #e8e8e8",
                                borderRadius: 16,
                                overflow: "hidden",
                                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                                background: "#F6F2ED"
                              }}
                            >
                              <div
                                style={{
                                  background: "#fff",
                                  padding: "8px 12px",
                                  borderBottom: "1px solid #f0f0f0",
                                  fontSize: 11,
                                  color: "#393735"
                                }}
                              >
                                ← Back
                              </div>
                              {previewImage && (
                                <div
                                  style={{
                                    height: 80,
                                    backgroundImage: `url(${previewImage})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center"
                                  }}
                                />
                              )}
                              <div
                                style={{
                                  background: "#fff",
                                  borderRadius: "12px 12px 0 0",
                                  marginTop: -8,
                                  position: "relative",
                                  padding: "12px 14px"
                                }}
                              >
                                {pCategory && (
                                  <span style={{ fontSize: 9, color: "#389BB4", fontWeight: 700 }}>
                                    {CATEGORY_LABELS[pCategory] || pCategory}
                                  </span>
                                )}
                                <Text strong style={{ fontSize: 13, color: "#393735", display: "block", marginTop: 2 }}>
                                  {pTitle || "Article Title"}
                                </Text>
                                {pDesc && (
                                  <Text
                                    style={{
                                      fontSize: 10,
                                      color: "#8F8C86",
                                      display: "block",
                                      marginTop: 4,
                                      lineHeight: 1.4
                                    }}
                                  >
                                    {pDesc.slice(0, 120)}
                                    {pDesc.length > 120 ? "..." : ""}
                                  </Text>
                                )}
                                <div style={{ marginTop: 8, borderTop: "1px solid #f0f0f0", paddingTop: 6 }}>
                                  <Text style={{ fontSize: 9, color: "#bfbfbf" }}>
                                    Article content will appear here...
                                  </Text>
                                </div>
                              </div>
                            </div>
                          </div>
                          {/* end scrollable content */}

                          {/* Bottom tab bar */}
                          <div
                            style={{
                              background: "#fff",
                              borderTop: "1px solid #f0f0f0",
                              display: "flex",
                              justifyContent: "space-around",
                              padding: "5px 0 6px",
                              flexShrink: 0
                            }}
                          >
                            {["Meds", "Assistance", "Wallet", "Education", "Help"].map((t, i) => (
                              <div key={t} style={{ textAlign: "center" }}>
                                <div
                                  style={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: "50%",
                                    background: i === 3 ? "#0C85A3" : "#EAE5DD",
                                    margin: "0 auto 1px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                  }}
                                >
                                  <span style={{ fontSize: 7, color: i === 3 ? "#fff" : "#8F8C86" }}>●</span>
                                </div>
                                <span
                                  style={{
                                    fontSize: 7,
                                    color: i === 3 ? "#393735" : "#8F8C86",
                                    fontWeight: i === 3 ? 700 : 400
                                  }}
                                >
                                  {t}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* end phone screen */}
                        <div
                          style={{
                            width: 44,
                            height: 4,
                            background: "#c0c0c0",
                            borderRadius: 10,
                            margin: "6px auto 2px"
                          }}
                        />
                      </div>
                      {/* end phone frame */}
                    </div>
                  );
                }}
              </Form.Item>
            </div>

            {/* ── Right: Form Fields ─────────────────────────────── */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Basic Info */}
              <Text strong style={{ display: "block", marginBottom: 12, fontSize: 15 }}>
                📝 Content Details
              </Text>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Form.Item name="brandJourneyConfiguration" label="Brand" rules={[{ required: true }]}>
                  <Select placeholder="Select brand">
                    {brands.map((b) => (
                      <Option key={b.id} value={b.id}>
                        {b.brandName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="category" label="Category" rules={[{ required: true, message: "Required" }]}>
                  <Select placeholder="Select category">
                    <Option value="financialTools">Financial Tools</Option>
                    <Option value="educationAndSupport">Education & Support</Option>
                    <Option value="communitiesAndAdvocacy">Communities & Advocacy</Option>
                    <Option value="logisticsAndTransport">Logistics & Transport</Option>
                    <Option value="wellness">Wellness</Option>
                    <Option value="expertAccess">Expert Access</Option>
                  </Select>
                </Form.Item>
              </div>
              <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                <Input placeholder="e.g., Things to know about injecting XOLAIR" />
              </Form.Item>
              <Form.Item name="description" label="Description">
                <TextArea rows={2} placeholder="Brief summary shown below the title..." />
              </Form.Item>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <Form.Item name="drugName" label="Drug Name">
                  <Input placeholder="e.g., Xolair" />
                </Form.Item>
                <Form.Item name="readTime" label="Read/Watch Time">
                  <Input placeholder="e.g., 5 min read" />
                </Form.Item>
                <Form.Item name="coverImageUrl" label="Cover Image">
                  <ImageUploadField
                    placeholder="URL or upload"
                    accept="image/jpeg,image/png,image/webp"
                    onPreviewChange={setCoverPreviewUrl}
                  />
                </Form.Item>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <Form.Item name="isFeatured" label="Featured" valuePropName="checked" initialValue={false}>
                  <Switch />
                </Form.Item>
                <Form.Item name="isActive" label="Active" valuePropName="checked" initialValue={true}>
                  <Switch />
                </Form.Item>
                <Form.Item
                  name="isPatientSendable"
                  label="Patient Content Center"
                  valuePropName="checked"
                  initialValue={false}
                  tooltip="Available for care team to send to patients"
                >
                  <Switch />
                </Form.Item>
              </div>

              <Divider />

              {/* Article Content */}
              <Text strong style={{ display: "block", marginBottom: 12, fontSize: 15 }}>
                📄 Article Content
              </Text>
              <Form.Item name="structuredContent">
                <StructuredContentEditor />
              </Form.Item>

              <Divider />

              {/* Targeting & Delivery */}
              <Text strong style={{ display: "block", marginBottom: 12, fontSize: 15 }}>
                🎯 Targeting & Delivery
              </Text>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Form.Item name="journeyStep" label="Journey Step">
                  <Select options={JOURNEY_STEPS} allowClear placeholder="Select step" />
                </Form.Item>
                <Form.Item name="triggerType" label="Event Trigger">
                  <Select options={TRIGGER_TYPES} />
                </Form.Item>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Form.Item name="triggerDays" label="Trigger Days">
                  <Input type="number" min={1} placeholder="e.g., 30" />
                </Form.Item>
                <Form.Item name="sortOrder" label="Sort Order" initialValue={0}>
                  <Input type="number" min={0} placeholder="0" />
                </Form.Item>
              </div>
              <Form.Item label="Delivery Channels">
                <Space>
                  <Form.Item name="channelEmail" valuePropName="checked" noStyle>
                    <Checkbox>Email</Checkbox>
                  </Form.Item>
                  <Form.Item name="channelSms" valuePropName="checked" noStyle>
                    <Checkbox>SMS</Checkbox>
                  </Form.Item>
                  <Form.Item name="channelInApp" valuePropName="checked" noStyle>
                    <Checkbox>In-App</Checkbox>
                  </Form.Item>
                </Space>
              </Form.Item>
            </div>
          </div>
        </Form>
      </Drawer>
    </PageContainer>
  );
};

export { TargetedEducationConfiguration };
